import { useMemo } from 'react';

import { useBackend } from '@/hooks/useBackend';
import { ApiCiamClientResponse } from '@/openapi/schema';
import { handleSWRErrorLocally } from '@/utils/swrOpenapiUtils';

export default function useCiamClients({
  apiSlug,
  portalClientIds,
  debouncedQuery,
}: {
  apiSlug: string;
  portalClientIds: Set<string>;
  debouncedQuery: string;
}): { ciamClients: ApiCiamClientResponse[]; isLoading: boolean; error: unknown } {
  const {
    data: ciamClients,
    isLoading: ciamClientsLoading,
    error: ciamClientsError,
  } = useBackend(
    '/apis/{apiSlug}/ciam-clients',
    { params: { path: { apiSlug } } },
    { revalidateIfStale: false, revalidateOnFocus: false, revalidateOnReconnect: false, ...handleSWRErrorLocally },
  );

  const nonPortalCiamClients = useMemo(
    () => ciamClients?.filter((ciamClient) => !portalClientIds.has(ciamClient.id)) ?? [],
    [ciamClients, portalClientIds],
  );

  const filteredCiamClients = useMemo(() => {
    if (nonPortalCiamClients.length === 0) return [];

    const lowerCaseQuery = debouncedQuery.toLowerCase();
    if (!lowerCaseQuery) {
      return nonPortalCiamClients;
    }

    return nonPortalCiamClients.filter((client) => {
      const clientName = client.name?.toLowerCase() || '';
      const clientId = client.id?.toLowerCase() || '';
      const liveMode = client.liveMode ? 'live' : 'test';
      const scopes = client.scopes ? client.scopes.map((scope) => scope.toLowerCase()) : [];

      return (
        clientName.includes(lowerCaseQuery) ||
        clientId.includes(lowerCaseQuery) ||
        liveMode.includes(lowerCaseQuery) ||
        scopes.some((scope) => scope.includes(lowerCaseQuery))
      );
    });
  }, [nonPortalCiamClients, debouncedQuery]);

  return { ciamClients: filteredCiamClients, isLoading: ciamClientsLoading, error: ciamClientsError };
}
